import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import {
  LOGO_PATH,
  LOGO_ICON_PATH,
  LOGO_ALT,
  STORE_NAME,
  STORE_TAGLINE,
} from '@/lib/brand'
import { StoreImage } from '@/store/components/StoreImage'

interface LogoProps {
  collapsed?: boolean
  className?: string
  size?: 'sm' | 'md' | 'lg'
  /** Show wordmark text beside the mark (icon mark only; full lockup already includes text). */
  showText?: boolean
  /** Storefront vs admin subtitle (icon mark only). */
  variant?: 'store' | 'admin'
  /** Gold gradient ring around the icon mark */
  gradientBorder?: boolean
  /** Show tagline under the wordmark (icon mark + showText only). Defaults to false for sm. */
  showTagline?: boolean
  /** Extra classes on the tagline line. */
  taglineClassName?: string
  /** Extra classes on the title + tagline block (icon mark + showText). */
  textClassName?: string
  /** Full lockup image or square emblem */
  mark?: 'full' | 'icon'
  /** Wrap in a home link */
  linkTo?: string
  /** Extra classes on the logo mark (image container). */
  markClassName?: string
  /** Wrapper around the icon mark (e.g. fixed box for scaled emblems). */
  iconWrapperClassName?: string
}

const fullHeights = {
  sm: 'h-10',
  md: 'h-14',
  lg: 'h-24',
}

const iconSizes = {
  sm: 'h-9 w-9',
  md: 'h-11 w-11',
  lg: 'h-16 w-16',
}

const textSizes = {
  sm: { title: 'text-base', sub: 'text-[9px]' },
  md: { title: 'text-lg', sub: 'text-[10px]' },
  lg: { title: 'text-2xl', sub: 'text-xs' },
}

const subtitles = {
  store: STORE_TAGLINE,
  admin: `${STORE_TAGLINE} — Admin`,
}

export function Logo({
  collapsed,
  className,
  size = 'md',
  showText = false,
  variant = 'admin',
  gradientBorder = false,
  mark = 'full',
  linkTo,
  markClassName,
  showTagline,
  taglineClassName,
  textClassName,
  iconWrapperClassName,
}: LogoProps) {
  const useIconMark = mark === 'icon' || collapsed
  const src = useIconMark ? LOGO_ICON_PATH : LOGO_PATH
  const displayText = showText && !collapsed && useIconMark
  const displayTagline = displayText && (showTagline ?? size !== 'sm')

  const logoMark =
    variant === 'store' && !useIconMark ? (
      <StoreImage
        src={src}
        alt={LOGO_ALT}
        priority
        objectFit="contain"
        sizing="box"
        spinnerSize="sm"
        containerClassName={cn(
          'shrink-0 overflow-hidden',
          fullHeights[size],
          'w-auto max-w-[min(100%,11rem)]',
          markClassName
        )}
        className="h-full w-auto max-h-full object-contain"
      />
    ) : (
      <img
        src={src}
        alt={LOGO_ALT}
        className={cn(
          'shrink-0 object-contain',
          useIconMark
            ? cn(
                gradientBorder
                  ? cn('h-full w-full rounded-full bg-white', markClassName)
                  : markClassName
                    ? cn('rounded-none bg-transparent object-contain', markClassName)
                    : cn(iconSizes[size], 'rounded-none bg-transparent'),
              )
            : cn(fullHeights[size], 'w-auto', markClassName)
        )}
      />
    )

  const logoImage =
    useIconMark && gradientBorder ? (
      <div
        className={cn(
          'shrink-0 rounded-full bg-gradient-to-br from-gold-100 via-gold-400 to-gold-700 p-[2.5px]',
          'shadow-[0_0_14px_rgba(212,175,55,0.35)]',
          iconSizes[size]
        )}
      >
        <div className="h-full w-full overflow-hidden rounded-full bg-white">
          {logoMark}
        </div>
      </div>
    ) : useIconMark && iconWrapperClassName ? (
      <div className={cn('shrink-0 overflow-visible', iconWrapperClassName)}>
        {logoMark}
      </div>
    ) : (
      logoMark
    )

  const content = (
    <div className={cn('flex items-center gap-3', className)}>
      {logoImage}
      {displayText && (
        <div className={cn('min-w-0', textClassName)}>
          <span
            className={cn(
              'block font-display font-bold leading-tight gold-gradient-text',
              textSizes[size].title
            )}
          >
            {STORE_NAME}
          </span>
          {displayTagline && (
            <span
              className={cn(
                'block text-gold-700/80 tracking-wide uppercase leading-snug',
                size === 'sm' ? 'text-[8px] line-clamp-2' : 'truncate',
                textSizes[size].sub,
                taglineClassName
              )}
            >
              {subtitles[variant]}
            </span>
          )}
        </div>
      )}
    </div>
  )

  if (linkTo) {
    return (
      <Link
        to={linkTo}
        aria-label={`${STORE_NAME} home`}
        className="inline-flex min-w-0 max-w-full transition-opacity hover:opacity-90"
      >
        {content}
      </Link>
    )
  }

  return content
}
